/**
 * Team Member Configuration
 *
 * Static team member data for offline fallback mode.
 * This config provides team member information when the backend is unavailable.
 *
 * Data Structure:
 * - id: Unique identifier (UUID format)
 * - name: Full name
 * - role: Job title (localized)
 * - bio: Professional biography (localized)
 * - imageUrl: Path to profile image
 * - skills: Array of technical skills
 * - experience: Professional experience description
 * - email: Contact email
 * - linkedin: LinkedIn profile URL
 * - xing: Xing profile URL
 * - github: GitHub profile URL (optional)
 */

export interface Certification {
  name: string;
  shortName: string;
  badgeUrl: string;
  issuer?: string;
}

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  imageUrl?: string;
  skills?: string[];
  experience?: string;
  email?: string;
  linkedin?: string;
  xing?: string;
  github?: string;
  certifications?: Certification[];
}

/**
 * Team member data organized by language code.
 *
 * Supported languages:
 * - en: English (default)
 * - de: German (Deutsch)
 * - fr: French (Français)
 * - es: Spanish (Español)
 * - pt: Portuguese (Português)
 * - hi: Hindi (हिन्दी)
 * - zh: Chinese Simplified (简体中文)
 */
export const teamMembers: Record<string, TeamMember[]> = {
  en: [
    {
      id: 'jan-pfrommer',
      name: 'Jan Pfrommer',
      role: 'AI Strategy & Implementation',
      bio: 'Senior Software Engineer and AI Expert with comprehensive experience spanning backend systems architecture, frontend development, and AI integration. As a certified Scrum Master, Jan has led development teams at Pforzheim University. He combines deep technical expertise in Context Engineering, RAG architectures, and AI Agents with hands-on software development experience. Currently co-developing bunker.ai, a local AI platform focused on data sovereignty and on-premise deployment.',
      imageUrl: '/jan.png',
      skills: [
        // AI & LLM - Primary Focus
        'Context Engineering',
        'AI Engineering',
        'Prompt Engineering',
        'RAG Architecture',
        'AI Agents',
        'Model Context Protocol (MCP)',
        'GitHub Copilot',
        'OpenAI',
        'DeepSeek R1',
        'Anthropic Claude',
        'Ollama',
        'LLMs',
        'Huggingface',
        'Pydantic AI',
        'Whisper',
        'RAG',
        'HiRAG',
        'Agentic AI',
        // Core Technologies
        'Python',
        'FastAPI',
        'TypeScript/Node.js',
        'React',
        'Java',
        'Spring Boot',
        'Flutter',
        'Vue.js',
        'Fastify',
        'Streamlit',
        // Databases
        'MySQL',
        'MariaDB',
        'MongoDB',
        'PostgreSQL',
        'Vector Databases',
        // Testing
        'JUnit/Mockito',
        'Jest',
        'K6 Load Testing',
        // Enterprise & Business
        'Agile Development with SCRUM',
        'BPMN 2.0',
        'Requirements Engineering',
        // Tools & DevOps
        'DevOps',
        'Docker',
        'Git',
        'CI/CD',
        'IntelliJ IDEA',
        'VS Code',
        'Postman'
      ],
      experience: '• Master of Science in Information Systems (Pforzheim University) – Thesis: Template-based Software Engineering for Web Applications using Web Components (Grade: 1.0)\n• Bachelor of Science in Business Informatics (Pforzheim University & KIT Karlsruhe)\n• Current: Co-founder & AI Engineer at securesight.ai – building bunker.ai\n• Languages: German (Native), English (Full Professional Proficiency C1)',
      email: 'jan.pfrommer@securesight.ai',
      linkedin: 'https://de.linkedin.com/in/jan-pfrommer-8632b8140',
      xing: 'https://www.xing.com/profile/Jan_Pfrommer041142',
      certifications: [
        {
          name: 'IREB Certified Professional for Requirements Engineering - Foundation Level',
          shortName: 'CPRE-FL',
          badgeUrl: 'https://www.mitsm.de/wp-content/uploads/2024/11/RZ_IREB_Badges_CPRE_TRAIN_2025-2.svg',
          issuer: 'IREB'
        },
        {
          name: 'ISTQB Certified Tester - Foundation Level',
          shortName: 'ISTQB CTFL',
          badgeUrl: 'https://logodix.com/logo/649849.jpg',
          issuer: 'ISTQB'
        },
        {
          name: 'SAP Certified Associate - Backend Developer - SAP Cloud Application Programming Model',
          shortName: 'SAP BTP',
          badgeUrl: 'https://images.credly.com/images/852e3e88-a0d3-4805-8ad9-6c2490ca2e20/image.png',
          issuer: 'SAP'
        }
      ]
    },
    {
      id: 'kai-bennett',
      name: 'Kai Bennett',
      role: 'Certified Software Architect & AI Engineer',
      bio: 'Certified Software Architect (iSAQB CPSA-F CPSA-A) and Senior Software Engineer specializing in C# .NET and on-premise AI architectures. Kai has a proven track record in RAG and HiRAG system implementations, enterprise AI application development, and building local AI infrastructure. He is co-developing bunker.ai with a focus on self-hosted AI solutions using open-source models.',
      imageUrl: '/kai.png',
      skills: [
        // AI & Local AI - Primary Focus
        'RAG Architecture',
        'RAG',
        'HiRAG',
        'AI Agents',
        'Agentic AI',
        'Document Intelligence',
        'Vector Databases',
        'Qdrant',
        'Prompt Engineering',
        // Core Technologies
        '.NET',
        'C#',
        'Python',
        'FastAPI',
        'JavaScript',
        'WPF',
        'SQL Server',
        'MySQL',
        // Architecture
        'Software Architecture',
        'Microservices',
        'DAPR',
        // DevOps
        'DevOps',
        'Docker',
        'Kubernetes',
        'CI/CD',
        'REST APIs'
      ],
      experience: '• Bachelor of Science in Applied Computer Science (FHDW Paderborn)\n• Certified Software Architect (iSAQB CPSA-F CPSA-A)\n• Senior Software Engineer\n• Current: Co-founder & Software Architect at securesight.ai – building bunker.ai\n• Expert in on-premise AI solutions and RAG/HiRAG implementations\n• Languages: German (C2 - Near Native), English (B2 - Fluent)',
      email: 'kai.bennett@securesight.ai',
      linkedin: 'https://de.linkedin.com/in/kai-bennett-698732276',
      xing: 'https://www.xing.com/profile/Kai_Bennett',
      certifications: [
        {
          name: 'iSAQB Certified Professional for Software Architecture - Foundation Level & Advanced Level',
          shortName: 'iSAQB CPSA-F/A',
          badgeUrl: 'https://eu.images.credential.net/badge/tiny/ku4bl7ci_1771016124466_badge.png',
          issuer: 'iSAQB'
        }
      ]
    }
  ],

  de: [
    {
      id: 'jan-pfrommer',
      name: 'Jan Pfrommer',
      role: 'KI-Strategie & Implementierung',
      bio: 'Senior Software Engineer und KI-Experte mit umfassender Erfahrung in Backend-Systemarchitektur, Frontend-Entwicklung und KI-Integration. Als zertifizierter Scrum Master hat Jan Entwicklungsteams an der Hochschule Pforzheim geleitet. Er verbindet tiefgehende technische Expertise in Context Engineering, RAG-Architekturen und KI-Agenten mit praktischer Softwareentwicklung. Aktuell entwickelt er bunker.ai, eine lokale KI-Plattform mit Fokus auf Datensouveränität und On-Premise-Deployment.',
      imageUrl: '/jan.png',
      skills: [
        // KI & LLM - Schwerpunkt
        'Context Engineering',
        'KI-Engineering',
        'Prompt-Engineering',
        'RAG-Architektur',
        'KI-Agenten',
        'Model Context Protocol (MCP)',
        'GitHub Copilot',
        'OpenAI',
        'DeepSeek R1',
        'Anthropic Claude',
        'Ollama',
        'LLMs',
        'Huggingface',
        'Pydantic AI',
        'Whisper',
        'RAG',
        'HiRAG',
        'Agentic AI',
        // Kern-Technologien
        'Python',
        'FastAPI',
        'TypeScript/Node.js',
        'React',
        'Java',
        'Spring Boot',
        'Flutter',
        'Vue.js',
        'Fastify',
        'Streamlit',
        // Datenbanken
        'MySQL',
        'MariaDB',
        'MongoDB',
        'PostgreSQL',
        'Vektor-Datenbanken',
        // Testing
        'JUnit/Mockito',
        'Jest',
        'K6 Lasttests',
        // Enterprise & Business
        'Agile Entwicklung mit SCRUM',
        'BPMN 2.0',
        'Requirements Engineering',
        // Tools & DevOps
        'DevOps',
        'Docker',
        'Git',
        'CI/CD',
        'IntelliJ IDEA',
        'VS Code',
        'Postman'
      ],
      experience: '• Master of Science in Information Systems (Hochschule Pforzheim) – Abschluss: Template-basiertes Software-Engineering von Web-Anwendungen unter Verwendung von Web-Komponenten (Note: 1,0)\n• Bachelor of Science in Wirtschaftsinformatik (Hochschule Pforzheim & KIT Karlsruhe)\n• Aktuell: Mitgründer & KI-Ingenieur bei securesight.ai – Entwicklung von bunker.ai\n• Sprachen: Deutsch (Muttersprache), Englisch (Verhandlungssicher C1)',
      email: 'jan.pfrommer@securesight.ai',
      linkedin: 'https://de.linkedin.com/in/jan-pfrommer-8632b8140',
      xing: 'https://www.xing.com/profile/Jan_Pfrommer041142',
      certifications: [
        {
          name: 'IREB Certified Professional for Requirements Engineering - Foundation Level',
          shortName: 'CPRE-FL',
          badgeUrl: 'https://www.mitsm.de/wp-content/uploads/2024/11/RZ_IREB_Badges_CPRE_TRAIN_2025-2.svg',
          issuer: 'IREB'
        },
        {
          name: 'ISTQB Certified Tester - Foundation Level',
          shortName: 'ISTQB CTFL',
          badgeUrl: 'https://logodix.com/logo/649849.jpg',
          issuer: 'ISTQB'
        },
        {
          name: 'SAP Certified Associate - Backend Developer - SAP Cloud Application Programming Model',
          shortName: 'SAP BTP',
          badgeUrl: 'https://images.credly.com/images/852e3e88-a0d3-4805-8ad9-6c2490ca2e20/image.png',
          issuer: 'SAP'
        }
      ]
    },
    {
      id: 'kai-bennett',
      name: 'Kai Bennett',
      role: 'Zertifizierter Software-Architekt & KI-Ingenieur',
      bio: 'Zertifizierter Software-Architekt (iSAQB CPSA-F CPSA-A) und Senior Software Engineer mit Spezialisierung auf C# .NET und lokale KI-Architekturen. Kai verfügt über eine nachgewiesene Erfolgsbilanz bei RAG- und HiRAG-System-Implementierungen, der Entwicklung von KI-Unternehmensanwendungen und dem Aufbau lokaler KI-Infrastruktur. Er entwickelt gemeinsam mit Jan bunker.ai mit Fokus auf selbstgehostete KI-Lösungen mit Open-Source-Modellen.',
      imageUrl: '/kai.png',
      skills: [
        // KI & Lokale KI - Schwerpunkt
        'RAG-Architektur',
        'RAG',
        'HiRAG',
        'KI-Agenten',
        'Agentic AI',
        'Document Intelligence',
        'Vektor-Datenbanken',
        'Qdrant',
        'Prompt-Engineering',
        // Kern-Technologien
        '.NET',
        'C#',
        'Python',
        'FastAPI',
        'JavaScript',
        'WPF',
        'SQL Server',
        'MySQL',
        // Architektur
        'Software-Architektur',
        'Microservices',
        'DAPR',
        // DevOps
        'DevOps',
        'Docker',
        'Kubernetes',
        'CI/CD',
        'REST APIs'
      ],
      experience: '• Bachelor of Science in Angewandte Informatik (FHDW Paderborn)\n• Zertifizierter Software-Architekt (iSAQB CPSA-F CPSA-A)\n• Senior Software Engineer\n• Aktuell: Mitgründer & Software-Architekt bei securesight.ai – Entwicklung von bunker.ai\n• Experte für lokale KI-Lösungen und RAG/HiRAG-Implementierungen\n• Sprachen: Deutsch (C2 - Muttersprache), Englisch (B2 - Fließend)',
      email: 'kai.bennett@securesight.ai',
      linkedin: 'https://de.linkedin.com/in/kai-bennett-698732276',
      xing: 'https://www.xing.com/profile/Kai_Bennett',
      certifications: [
        {
          name: 'iSAQB Certified Professional for Software Architecture - Foundation Level & Advanced Level',
          shortName: 'iSAQB CPSA-F/A',
          badgeUrl: 'https://eu.images.credential.net/badge/tiny/ku4bl7ci_1771016124466_badge.png',
          issuer: 'iSAQB'
        }
      ]
    }
  ],

  fr: [
    {
      id: 'jan-pfrommer',
      name: 'Jan Pfrommer',
      role: 'Stratégie IA & Mise en œuvre',
      bio: 'Ingénieur logiciel senior et expert IA avec une expérience approfondie en architecture backend, développement frontend et intégration IA. En tant que Scrum Master certifié, Jan a dirigé des équipes de développement à l\'université de Pforzheim. Il combine une expertise technique approfondie en Context Engineering, architectures RAG et agents IA avec une expérience pratique en développement logiciel.',
      imageUrl: '/jan.png',
      skills: [
        // IA & LLM - Focus principal
        'Context Engineering',
        'Ingénierie IA',
        'Prompt Engineering',
        'Architecture RAG',
        'Agents IA',
        'Model Context Protocol (MCP)',
        'GitHub Copilot',
        'OpenAI',
        'DeepSeek R1',
        'Anthropic Claude',
        'Ollama',
        'LLMs',
        'Huggingface',
        'Pydantic AI',
        'Whisper',
        'RAG',
        'HiRAG',
        'Agentic AI',
        // Technologies Core
        'Python',
        'FastAPI',
        'TypeScript/Node.js',
        'React',
        'Java',
        'Spring Boot',
        'Flutter',
        'Vue.js',
        'Fastify',
        'Streamlit',
        // Bases de données
        'MySQL',
        'MariaDB',
        'MongoDB',
        'PostgreSQL',
        'Bases de données vectorielles',
        // Tests
        'JUnit/Mockito',
        'Jest',
        'K6 Tests de charge',
        // Enterprise & Business
        'Développement Agile avec SCRUM',
        'BPMN 2.0',
        'Requirements Engineering',
        // Outils & DevOps
        'DevOps',
        'Docker',
        'Git',
        'CI/CD',
        'IntelliJ IDEA',
        'VS Code',
        'Postman'
      ],
      experience: '• Master of Science en Systèmes d\'Information (Université de Pforzheim) – Thèse : Ingénierie logicielle basée sur des modèles pour applications Web (Note : 1,0)\n• Bachelor of Science en Business Informatics (Université de Pforzheim & KIT Karlsruhe)\n• Actuel : Co-fondateur & Ingénieur IA chez securesight.ai – développement de bunker.ai\n• Langues : Allemand (Langue maternelle), Anglais (C1 - Professionnel)',
      email: 'jan.pfrommer@securesight.ai',
      linkedin: 'https://de.linkedin.com/in/jan-pfrommer-8632b8140',
      xing: 'https://www.xing.com/profile/Jan_Pfrommer041142',
      certifications: [
        {
          name: 'IREB Certified Professional for Requirements Engineering - Foundation Level',
          shortName: 'CPRE-FL',
          badgeUrl: 'https://www.mitsm.de/wp-content/uploads/2024/11/RZ_IREB_Badges_CPRE_TRAIN_2025-2.svg',
          issuer: 'IREB'
        },
        {
          name: 'ISTQB Certified Tester - Foundation Level',
          shortName: 'ISTQB CTFL',
          badgeUrl: 'https://logodix.com/logo/649849.jpg',
          issuer: 'ISTQB'
        },
        {
          name: 'SAP Certified Associate - Backend Developer',
          shortName: 'SAP BTP',
          badgeUrl: 'https://images.credly.com/images/852e3e88-a0d3-4805-8ad9-6c2490ca2e20/image.png',
          issuer: 'SAP'
        }
      ]
    },
    {
      id: 'kai-bennett',
      name: 'Kai Bennett',
      role: 'Architecte Logiciel Certifié & Ingénieur IA',
      bio: 'Architecte logiciel certifié (iSAQB CPSA-F CPSA-A) et ingénieur logiciel senior spécialisé en C# .NET et architectures IA locales. Kai possède un bilan prouvé dans les implémentations de systèmes RAG et HiRAG, le développement d\'applications IA d\'entreprise et la construction d\'infrastructure IA locale. Il co-développe bunker.ai avec un focus sur les solutions IA auto-hébergées utilisant des modèles open-source.',
      imageUrl: '/kai.png',
      skills: [
        // AI & Local AI
        'Architecture RAG',
        'RAG',
        'HiRAG',
        'Agents IA',
        'Agentic AI',
        'Document Intelligence',
        'Bases de données vectorielles',
        'Qdrant',
        'Prompt Engineering',
        // Technologies Core
        '.NET',
        'C#',
        'Python',
        'FastAPI',
        'JavaScript',
        'WPF',
        'SQL Server',
        'MySQL',
        // Architecture
        'Architecture Logicielle',
        'Microservices',
        'DAPR',
        // DevOps
        'DevOps',
        'Docker',
        'Kubernetes',
        'CI/CD',
        'REST APIs'
      ],
      experience: '• Bachelor of Science en Informatique Appliquée (FHDW Paderborn)\n• Architecte Logiciel Certifié (iSAQB CPSA-F CPSA-A)\n• Ingénieur Logiciel Senior\n• Actuel : Co-fondateur & Architecte Logiciel chez securesight.ai – développement de bunker.ai\n• Expert en solutions IA locales et implémentations RAG/HiRAG\n• Langues : Allemand (C2 - Quasi natif), Anglais (B2 - Courant)',
      email: 'kai.bennett@securesight.ai',
      linkedin: 'https://de.linkedin.com/in/kai-bennett-698732276',
      xing: 'https://www.xing.com/profile/Kai_Bennett',
      certifications: [
        {
          name: 'iSAQB Certified Professional for Software Architecture - Foundation Level & Advanced Level',
          shortName: 'iSAQB CPSA-F/A',
          badgeUrl: 'https://eu.images.credential.net/badge/tiny/ku4bl7ci_1771016124466_badge.png',
          issuer: 'iSAQB'
        }
      ]
    }
  ],

  es: [
    {
      id: 'jan-pfrommer',
      name: 'Jan Pfrommer',
      role: 'Estrategia IA e Implementación',
      bio: 'Ingeniero de software senior y experto en IA con experiencia integral en arquitectura backend, desarrollo frontend e integración de IA. Como Scrum Master certificado, Jan ha liderado equipos de desarrollo en la Universidad de Pforzheim mientras desarrolla soluciones de software empresariales. Combina experiencia técnica profunda en Context Engineering, arquitecturas RAG y agentes IA con experiencia práctica en desarrollo de software.',
      imageUrl: '/jan.png',
      skills: [
        // IA & LLM - Enfoque principal
        'Context Engineering',
        'Ingeniería IA',
        'Prompt Engineering',
        'Arquitectura RAG',
        'Agentes IA',
        'Model Context Protocol (MCP)',
        'GitHub Copilot',
        'OpenAI',
        'DeepSeek R1',
        'Anthropic Claude',
        'Ollama',
        'LLMs',
        'Huggingface',
        'Pydantic AI',
        'Whisper',
        'RAG',
        'HiRAG',
        'Agentic AI',
        // Tecnologías Core
        'Python',
        'FastAPI',
        'TypeScript/Node.js',
        'React',
        'Java',
        'Spring Boot',
        'Flutter',
        'Vue.js',
        'Fastify',
        'Streamlit',
        // Bases de datos
        'MySQL',
        'MariaDB',
        'MongoDB',
        'PostgreSQL',
        'Bases de datos vectoriales',
        // Testing
        'JUnit/Mockito',
        'Jest',
        'Tests de carga K6',
        // Enterprise & Business
        'Desarrollo Ágil con SCRUM',
        'BPMN 2.0',
        'Requirements Engineering',
        // Herramientas & DevOps
        'DevOps',
        'Docker',
        'Git',
        'CI/CD',
        'IntelliJ IDEA',
        'VS Code',
        'Postman'
      ],
      experience: '• Master of Science en Sistemas de Información (Universidad de Pforzheim) – Tesis: Ingeniería de software basada en plantillas para aplicaciones web (Calificación: 1,0)\n• Bachelor of Science en Informática de Negocios (Universidad de Pforzheim & KIT Karlsruhe)\n• Actual: Co-fundador & Ingeniero IA en securesight.ai – desarrollo de bunker.ai\n• Idiomas: Alemán (Lengua materna), Inglés (C1 - Profesional)',
      email: 'jan.pfrommer@securesight.ai',
      linkedin: 'https://de.linkedin.com/in/jan-pfrommer-8632b8140',
      xing: 'https://www.xing.com/profile/Jan_Pfrommer041142',
      certifications: [
        {
          name: 'IREB Certified Professional for Requirements Engineering - Foundation Level',
          shortName: 'CPRE-FL',
          badgeUrl: 'https://www.mitsm.de/wp-content/uploads/2024/11/RZ_IREB_Badges_CPRE_TRAIN_2025-2.svg',
          issuer: 'IREB'
        },
        {
          name: 'ISTQB Certified Tester - Foundation Level',
          shortName: 'ISTQB CTFL',
          badgeUrl: 'https://logodix.com/logo/649849.jpg',
          issuer: 'ISTQB'
        },
        {
          name: 'SAP Certified Associate - Backend Developer',
          shortName: 'SAP BTP',
          badgeUrl: 'https://images.credly.com/images/852e3e88-a0d3-4805-8ad9-6c2490ca2e20/image.png',
          issuer: 'SAP'
        }
      ]
    },
    {
      id: 'kai-bennett',
      name: 'Kai Bennett',
      role: 'Arquitecto de Software Certificado & Ingeniero IA',
      bio: 'Arquitecto de software certificado (iSAQB CPSA-F CPSA-A) e ingeniero de software senior especializado en C# .NET y arquitecturas IA locales. Kai tiene un historial comprobado en implementaciones de sistemas RAG y HiRAG, desarrollo de aplicaciones IA empresariales y construcción de infraestructura IA local. Co-desarrolla bunker.ai con enfoque en soluciones IA auto-alojadas usando modelos open-source.',
      imageUrl: '/kai.png',
      skills: [
        // AI & Local AI
        'Arquitectura RAG',
        'RAG',
        'HiRAG',
        'Agentes IA',
        'Agentic AI',
        'Document Intelligence',
        'Bases de datos vectoriales',
        'Qdrant',
        'Prompt Engineering',
        // Tecnologías Core
        '.NET',
        'C#',
        'Python',
        'FastAPI',
        'JavaScript',
        'WPF',
        'SQL Server',
        'MySQL',
        // Arquitectura
        'Arquitectura de Software',
        'Microservices',
        'DAPR',
        // DevOps
        'DevOps',
        'Docker',
        'Kubernetes',
        'CI/CD',
        'REST APIs'
      ],
      experience: '• Bachelor of Science en Informática Aplicada (FHDW Paderborn)\n• Arquitecto de Software Certificado (iSAQB CPSA-F CPSA-A)\n• Ingeniero de Software Senior\n• Actual: Co-fundador & Arquitecto de Software en securesight.ai – desarrollo de bunker.ai\n• Experto en soluciones IA locales e implementaciones RAG/HiRAG\n• Idiomas: Alemán (C2 - Casi nativo), Inglés (B2 - Fluido)',
      email: 'kai.bennett@securesight.ai',
      linkedin: 'https://de.linkedin.com/in/kai-bennett-698732276',
      xing: 'https://www.xing.com/profile/Kai_Bennett',
      certifications: [
        {
          name: 'iSAQB Certified Professional for Software Architecture - Foundation Level & Advanced Level',
          shortName: 'iSAQB CPSA-F/A',
          badgeUrl: 'https://eu.images.credential.net/badge/tiny/ku4bl7ci_1771016124466_badge.png',
          issuer: 'iSAQB'
        }
      ]
    }
  ],

  pt: [
    {
      id: 'jan-pfrommer',
      name: 'Jan Pfrommer',
      role: 'Estratégia IA e Implementação',
      bio: 'Engenheiro de software sênior e especialista em IA com experiência abrangente em arquitetura backend, desenvolvimento frontend e integração de IA. Como Scrum Master certificado, Jan liderou equipes de desenvolvimento na Universidade de Pforzheim enquanto desenvolve soluções de software empresariais. Combina experiência técnica profunda em Context Engineering, arquiteturas RAG e agentes IA com experiência prática em desenvolvimento de software.',
      imageUrl: '/jan.png',
      skills: [
        // IA & LLM - Foco principal
        'Context Engineering',
        'Engenharia IA',
        'Prompt Engineering',
        'Arquitetura RAG',
        'Agentes IA',
        'Model Context Protocol (MCP)',
        'GitHub Copilot',
        'OpenAI',
        'DeepSeek R1',
        'Anthropic Claude',
        'Ollama',
        'LLMs',
        'Huggingface',
        'Pydantic AI',
        'Whisper',
        'RAG',
        'HiRAG',
        'Agentic AI',
        // Tecnologias Core
        'Python',
        'FastAPI',
        'TypeScript/Node.js',
        'React',
        'Java',
        'Spring Boot',
        'Flutter',
        'Vue.js',
        'Fastify',
        'Streamlit',
        // Bancos de dados
        'MySQL',
        'MariaDB',
        'MongoDB',
        'PostgreSQL',
        'Bancos de dados vetoriais',
        // Testes
        'JUnit/Mockito',
        'Jest',
        'Testes de carga K6',
        // Enterprise & Business
        'Desenvolvimento Ágil com SCRUM',
        'BPMN 2.0',
        'Requirements Engineering',
        // Ferramentas & DevOps
        'DevOps',
        'Docker',
        'Git',
        'CI/CD',
        'IntelliJ IDEA',
        'VS Code',
        'Postman'
      ],
      experience: '• Master of Science em Sistemas de Informação (Universidade de Pforzheim) – Tese: Engenharia de software baseada em modelos para aplicações web (Nota: 1,0)\n• Bachelor of Science em Informática de Negócios (Universidade de Pforzheim & KIT Karlsruhe)\n• Atual: Co-fundador & Engenheiro IA em securesight.ai – desenvolvimento de bunker.ai\n• Idiomas: Alemão (Língua materna), Inglês (C1 - Profissional)',
      email: 'jan.pfrommer@securesight.ai',
      linkedin: 'https://de.linkedin.com/in/jan-pfrommer-8632b8140',
      xing: 'https://www.xing.com/profile/Jan_Pfrommer041142',
      certifications: [
        {
          name: 'IREB Certified Professional for Requirements Engineering - Foundation Level',
          shortName: 'CPRE-FL',
          badgeUrl: 'https://www.mitsm.de/wp-content/uploads/2024/11/RZ_IREB_Badges_CPRE_TRAIN_2025-2.svg',
          issuer: 'IREB'
        },
        {
          name: 'ISTQB Certified Tester - Foundation Level',
          shortName: 'ISTQB CTFL',
          badgeUrl: 'https://logodix.com/logo/649849.jpg',
          issuer: 'ISTQB'
        },
        {
          name: 'SAP Certified Associate - Backend Developer',
          shortName: 'SAP BTP',
          badgeUrl: 'https://images.credly.com/images/852e3e88-a0d3-4805-8ad9-6c2490ca2e20/image.png',
          issuer: 'SAP'
        }
      ]
    },
    {
      id: 'kai-bennett',
      name: 'Kai Bennett',
      role: 'Arquiteto de Software Certificado & Engenheiro IA',
      bio: 'Arquiteto de software certificado (iSAQB CPSA-F CPSA-A) e engenheiro de software sênior especializado em C# .NET e arquiteturas IA locais. Kai possui um histórico comprovado em implementações de sistemas RAG e HiRAG, desenvolvimento de aplicações IA empresariais e construção de infraestrutura IA local. Co-desenvolve bunker.ai com foco em soluções IA auto-hospedadas usando modelos open-source.',
      imageUrl: '/kai.png',
      skills: [
        // AI & Local AI
        'Arquitetura RAG',
        'RAG',
        'HiRAG',
        'Agentes IA',
        'Agentic AI',
        'Document Intelligence',
        'Bancos de dados vetoriais',
        'Qdrant',
        'Prompt Engineering',
        // Tecnologias Core
        '.NET',
        'C#',
        'Python',
        'FastAPI',
        'JavaScript',
        'WPF',
        'SQL Server',
        'MySQL',
        // Arquitetura
        'Arquitetura de Software',
        'Microservices',
        'DAPR',
        // DevOps
        'DevOps',
        'Docker',
        'Kubernetes',
        'CI/CD',
        'REST APIs'
      ],
      experience: '• Bachelor of Science em Informática Aplicada (FHDW Paderborn)\n• Arquiteto de Software Certificado (iSAQB CPSA-F CPSA-A)\n• Engenheiro de Software Sênior\n• Atual: Co-fundador & Arquiteto de Software em securesight.ai – desenvolvimento de bunker.ai\n• Especialista em soluções IA locais e implementações RAG/HiRAG\n• Idiomas: Alemão (C2 - Quase nativo), Inglês (B2 - Fluente)',
      email: 'kai.bennett@securesight.ai',
      linkedin: 'https://de.linkedin.com/in/kai-bennett-698732276',
      xing: 'https://www.xing.com/profile/Kai_Bennett',
      certifications: [
        {
          name: 'iSAQB Certified Professional for Software Architecture - Foundation Level & Advanced Level',
          shortName: 'iSAQB CPSA-F/A',
          badgeUrl: 'https://eu.images.credential.net/badge/tiny/ku4bl7ci_1771016124466_badge.png',
          issuer: 'iSAQB'
        }
      ]
    }
  ],

  hi: [
    {
      id: 'jan-pfrommer',
      name: 'जान प्फ्रोमर',
      role: 'एआई रणनीति और कार्यान्वयन',
      bio: 'सीनियर सॉफ्टवेयर इंजीनियर और एआई विशेषज्ञ जिनके पास बैकएंड सिस्टम आर्किटेक्चर, फ्रंटएंड डेवलपमेंट और एआई एकीकरण में व्यापक अनुभव है। एक प्रमाणित स्क्रम मास्टर के रूप में, जान ने प्रफोर्ज़हाइम विश्वविद्यालय में विकास टीमों का नेतृत्व किया है और एंटरप्राइज़ सॉफ्टवेयर समाधान विकसित करते हैं। वे Context Engineering, RAG आर्किटेक्चर और AI Agents में गहरी तकनीकी विशेषज्ञता को व्यावहारिक सॉफ्टवेयर विकास के साथ जोड़ते हैं।',
      imageUrl: '/jan.png',
      skills: [
        // AI & LLM - मुख्य फोकस
        'Context Engineering',
        'AI Engineering',
        'Prompt Engineering',
        'RAG Architecture',
        'AI Agents',
        'Model Context Protocol (MCP)',
        'GitHub Copilot',
        'OpenAI',
        'DeepSeek R1',
        'Anthropic Claude',
        'Ollama',
        'LLMs',
        'Huggingface',
        'Pydantic AI',
        'Whisper',
        // कोर तकनीकें
        'Python',
        'FastAPI',
        'TypeScript/Node.js',
        'React',
        'Java',
        'Spring Boot',
        'Flutter',
        'Vue.js',
        'Streamlit',
        // डेटाबेस
        'MySQL',
        'MariaDB',
        'MongoDB',
        'PostgreSQL',
        'वेक्टर डेटाबेस',
        // टेस्टिंग
        'JUnit/Mockito',
        'Jest',
        // एंटरप्राइज़ और बिजनेस
        'SCRUM के साथ एजाइल विकास',
        'BPMN 2.0',
        'Requirements Engineering',
        // टूल्स और DevOps
        'Docker',
        'Git',
        'IntelliJ IDEA',
        'VS Code',
        'Postman'
      ],
      experience: '• मास्टर ऑफ साइंस इन इन्फॉर्मेशन सिस्टम्स (प्रफोर्ज़हाइम विश्वविद्यालय) – थीसिस: वेब कंपोनेंट के लिए टेम्पलेट-आधारित सॉफ्टवेयर इंजीनियरिंग (ग्रेड: 1.0)\n• बैचलर ऑफ साइंस इन बिजनेस इन्फॉर्मेटिक्स (प्रफोर्ज़हाइम विश्वविद्यालय और KIT कार्ल्सरूह)\n• वर्तमान: securesight.ai में सह-संस्थापक और AI इंजीनियर – bunker.ai का विकास\n• भाषाएं: जर्मन (मातृभाषा), अंग्रेजी (C1 - पेशेवर)',
      email: 'jan.pfrommer@securesight.ai',
      linkedin: 'https://de.linkedin.com/in/jan-pfrommer-8632b8140',
      xing: 'https://www.xing.com/profile/Jan_Pfrommer041142',
      certifications: [
        {
          name: 'IREB Certified Professional for Requirements Engineering - Foundation Level',
          shortName: 'CPRE-FL',
          badgeUrl: 'https://www.mitsm.de/wp-content/uploads/2024/11/RZ_IREB_Badges_CPRE_TRAIN_2025-2.svg',
          issuer: 'IREB'
        },
        {
          name: 'ISTQB Certified Tester - Foundation Level',
          shortName: 'ISTQB CTFL',
          badgeUrl: 'https://logodix.com/logo/649849.jpg',
          issuer: 'ISTQB'
        },
        {
          name: 'SAP Certified Associate - Backend Developer',
          shortName: 'SAP BTP',
          badgeUrl: 'https://images.credly.com/images/852e3e88-a0d3-4805-8ad9-6c2490ca2e20/image.png',
          issuer: 'SAP'
        }
      ]
    },
    {
      id: 'kai-bennett',
      name: 'काई बेनेट',
      role: 'प्रमाणित सॉफ्टवेयर आर्किटेक्ट और AI इंजीनियर',
      bio: 'C# .NET और स्थानीय AI आर्किटेक्चर में विशेषज्ञता रखने वाले प्रमाणित सॉफ्टवेयर आर्किटेक्ट (iSAQB CPSA-F CPSA-A) और सीनियर सॉफ्टवेयर इंजीनियर। काई के पास RAG और HiRAG सिस्टम इम्प्लीमेंटेशन और एंटरप्राइज़ AI एप्लिकेशन डेवलपमेंट में सिद्ध ट्रैक रिकॉर्ड है। वे जान के साथ bunker.ai का सह-विकास कर रहे हैं, जो ओपन-सोर्स मॉडल पर आधारित स्व-होस्टेड AI समाधानों पर केंद्रित है।',
      imageUrl: '/kai.png',
      skills: [
        // AI - मुख्य फोकस
        'RAG Architecture',
        'RAG',
        'HiRAG',
        'AI Agents',
        'Agentic AI',
        'Document Intelligence',
        'वेक्टर डेटाबेस',
        'Qdrant',
        'Prompt Engineering',
        // कोर तकनीकें
        '.NET',
        'C#',
        'Python',
        'FastAPI',
        'JavaScript',
        'WPF',
        'SQL Server',
        'MySQL',
        // आर्किटेक्चर
        'सॉफ्टवेयर आर्किटेक्चर',
        'Microservices',
        'DAPR',
        // DevOps
        'DevOps',
        'Docker',
        'Kubernetes',
        'CI/CD',
        'REST APIs'
      ],
      experience: '• एप्लाइड कंप्यूटर साइंस में बैचलर ऑफ साइंस (FHDW पैडरबोर्न)\n• प्रमाणित सॉफ्टवेयर आर्किटेक्ट (iSAQB CPSA-F CPSA-A)\n• में सीनियर सॉफ्टवेयर इंजीनियर\n• वर्तमान: securesight.ai में सह-संस्थापक और सॉफ्टवेयर आर्किटेक्ट – bunker.ai का विकास\n• स्थानीय AI समाधान और RAG/HiRAG इम्प्लीमेंटेशन में विशेषज्ञ\n• भाषाएं: जर्मन (C2 - लगभग मातृभाषा), अंग्रेजी (B2 - धाराप्रवाह)',
      email: 'kai.bennett@securesight.ai',
      linkedin: 'https://de.linkedin.com/in/kai-bennett-698732276',
      xing: 'https://www.xing.com/profile/Kai_Bennett',
      certifications: [
        {
          name: 'iSAQB Certified Professional for Software Architecture - Foundation Level & Advanced Level',
          shortName: 'iSAQB CPSA-F/A',
          badgeUrl: 'https://eu.images.credential.net/badge/tiny/ku4bl7ci_1771016124466_badge.png',
          issuer: 'iSAQB'
        }
      ]
    }
  ],

  zh: [
    {
      id: 'jan-pfrommer',
      name: 'Jan Pfrommer',
      role: 'AI战略与实施',
      bio: '高级软件工程师和AI专家，在后端系统架构、前端开发和AI集成方面拥有全面经验。作为认证Scrum Master，Jan曾在普福尔茨大学领导开发团队，同时开发企业级软件解决方案。他将上下文工程、RAG架构和AI代理方面的深厚技术专长与实际软件开发经验相结合。',
      imageUrl: '/jan.png',
      skills: [
        // AI & LLM - 主要专注
        'Context Engineering',
        'AI Engineering',
        'Prompt Engineering',
        'RAG Architecture',
        'AI Agents',
        'Model Context Protocol (MCP)',
        'GitHub Copilot',
        'OpenAI',
        'DeepSeek R1',
        'Anthropic Claude',
        'Ollama',
        'LLMs',
        'Huggingface',
        'Pydantic AI',
        'Whisper',
        'RAG',
        'HiRAG',
        'Agentic AI',
        // 核心技术
        'Python',
        'FastAPI',
        'TypeScript/Node.js',
        'React',
        'Java',
        'Spring Boot',
        'Flutter',
        'Vue.js',
        'Fastify',
        'Streamlit',
        // 数据库
        'MySQL',
        'MariaDB',
        'MongoDB',
        'PostgreSQL',
        '向量数据库',
        // 测试
        'JUnit/Mockito',
        'Jest',
        'K6负载测试',
        // 企业 & 业务
        'SCRUM 敏捷开发',
        'BPMN 2.0',
        'Requirements Engineering',
        // 工具 & DevOps
        'DevOps',
        'Docker',
        'Git',
        'CI/CD',
        'IntelliJ IDEA',
        'VS Code',
        'Postman'
      ],
      experience: '• 信息系统硕士（普福尔茨大学）– 论文：基于Web组件的应用程序模板化软件工程（成绩：1.0）\n• 商业信息学学士（普福尔茨大学 & 卡尔斯鲁厄理工学院）\n• 当前：securesight.ai联合创始人和AI工程师 – 开发bunker.ai\n• 语言：德语（母语）、英语（C1 - 专业流利）',
      email: 'jan.pfrommer@securesight.ai',
      linkedin: 'https://de.linkedin.com/in/jan-pfrommer-8632b8140',
      xing: 'https://www.xing.com/profile/Jan_Pfrommer041142',
      certifications: [
        {
          name: 'IREB Certified Professional for Requirements Engineering - Foundation Level',
          shortName: 'CPRE-FL',
          badgeUrl: 'https://www.mitsm.de/wp-content/uploads/2024/11/RZ_IREB_Badges_CPRE_TRAIN_2025-2.svg',
          issuer: 'IREB'
        },
        {
          name: 'ISTQB Certified Tester - Foundation Level',
          shortName: 'ISTQB CTFL',
          badgeUrl: 'https://logodix.com/logo/649849.jpg',
          issuer: 'ISTQB'
        },
        {
          name: 'SAP Certified Associate - Backend Developer',
          shortName: 'SAP BTP',
          badgeUrl: 'https://images.credly.com/images/852e3e88-a0d3-4805-8ad9-6c2490ca2e20/image.png',
          issuer: 'SAP'
        }
      ]
    },
    {
      id: 'kai-bennett',
      name: 'Kai Bennett',
      role: '认证软件架构师 & AI工程师',
      bio: '专注于C# .NET和本地AI架构的认证软件架构师（iSAQB CPSA-F CPSA-A）和高级软件工程师。Kai在RAG和HiRAG系统实施和企业AI应用开发方面拥有经过验证的成功记录。他正与Jan共同开发bunker.ai，专注于使用开源模型的自托管AI解决方案。',
      imageUrl: '/kai.png',
      skills: [
        // AI - 主要专注
        'RAG Architecture',
        'RAG',
        'HiRAG',
        'AI Agents',
        'Agentic AI',
        'Document Intelligence',
        '向量数据库',
        'Qdrant',
        'Prompt Engineering',
        // 核心技术
        '.NET',
        'C#',
        'Python',
        'FastAPI',
        'JavaScript',
        'WPF',
        'SQL Server',
        'MySQL',
        // 架构
        '软件架构',
        'Microservices',
        'DAPR',
        // DevOps
        'DevOps',
        'Docker',
        'Kubernetes',
        'CI/CD',
        'REST APIs'
      ],
      experience: '• 应用计算机科学学士（FHDW帕德博恩）\n• 认证软件架构师（iSAQB CPSA-F CPSA-A）\n• 高级软件工程师\n• 当前：securesight.ai联合创始人和软件架构师 – 开发bunker.ai\n• 本地AI解决方案和RAG/HiRAG实施专家\n• 语言：德语（C2 - 接近母语）、英语（B2 - 流利）',
      email: 'kai.bennett@securesight.ai',
      linkedin: 'https://de.linkedin.com/in/kai-bennett-698732276',
      xing: 'https://www.xing.com/profile/Kai_Bennett',
      certifications: [
        {
          name: 'iSAQB Certified Professional for Software Architecture - Foundation Level & Advanced Level',
          shortName: 'iSAQB CPSA-F/A',
          badgeUrl: 'https://eu.images.credential.net/badge/tiny/ku4bl7ci_1771016124466_badge.png',
          issuer: 'iSAQB'
        }
      ]
    }
  ]
};

/**
 * Get team members for a specific language.
 * Falls back to English if language is not available.
 *
 * @param lang - Language code (en, de, fr, es, pt, hi, zh)
 * @returns Array of team members in specified language
 */
export function getTeamMembers(lang: string = 'en'): TeamMember[] {
  return teamMembers[lang] || teamMembers.en;
}

/**
 * Get a single team member by ID for a specific language.
 * Returns undefined if member is not found.
 *
 * @param id - Team member ID
 * @param lang - Language code (defaults to 'en')
 * @returns Team member object or undefined
 */
export function getTeamMemberById(id: string, lang: string = 'en'): TeamMember | undefined {
  const members = getTeamMembers(lang);
  return members.find(member => member.id === id);
}

/**
 * Default export for backward compatibility
 */
export default teamMembers;
